import React, { FC, useState, useEffect, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import Box from "@mui/material/Box";
import { useSelector, useDispatch } from "react-redux";

import TopHeader from "./components/TopHeader/TopHeader";
import MiniCartItem from "./components/MiniCartItem/MiniCartItem";
import EmptyCartView from "./components/EmptyCartView/EmptyCartView";
import BottomActions from "./components/BottomActions/BottomActions";
import { CartPendingIndicator } from "../cart/CartPendingIndicator";
import { CartErrorRecovery } from "../cart/CartErrorRecovery";

import { useDebounceCartUpdates } from "../../hooks/useDebounceCartUpdates";
import { usePageUnloadHandler } from "../../hooks/usePageUnloadHandler";
import CartAutoFlushService from "../../services/CartAutoFlushService";
import {
  cleanupStaleOptimisticUpdates,
  removeOptimisticProduct,
} from "../../redux/cartReducer";
import {
  calculateItemsSubtotal,
  hasPromotionalDiscounts,
} from "../../utils/cartCalculationUtils";
import {
  logSubtotalChange,
  logOptimisticStateChange,
} from "../../utils/optimisticLogger";

import styles from "./MiniCart.module.css";
import { lockBodyScroll, unlockBodyScroll } from "../../utils/scrollUtils";

type Props = { toggleSidenav: () => void };

const MiniCart: FC<Props> = ({ toggleSidenav }) => {
  const [isVisible, setIsVisible] = useState(false);
  const dispatch = useDispatch();
  const navigate = useNavigate();

  // Use Redux store for cart data
  const cart = useSelector((state: any) => state.cart);
  const { groups, optimisticProducts, totals } = cart;

  // Use the shared debounce hook for cart updates
  const {
    handleIncrease,
    handleDecrease,
    handleRemove,
    getOptimisticQuantity,
    getOptimisticPrice,
    getOptimisticTotalPrice,
    flushUpdates,
    hasPendingUpdates,
    getPendingUpdatesCount,
  } = useDebounceCartUpdates(1000);

  // Handle page unload to prevent loss of pending cart operations
  usePageUnloadHandler(flushUpdates, hasPendingUpdates);

  // Cleanup function that runs on mount and periodically
  const cleanupStaleUpdates = useCallback(() => {
    if (optimisticProducts?.length > 0) {
      console.log(
        `🔍 Checking ${optimisticProducts.length} optimistic updates for stale entries`
      );
      dispatch(cleanupStaleOptimisticUpdates());
    }
  }, [dispatch, optimisticProducts?.length]);

  // Run cleanup when component mounts and whenever optimisticProducts changes
  useEffect(() => {
    cleanupStaleUpdates();

    // Register with auto-flush service
    const autoFlushService = CartAutoFlushService.getInstance();
    const unregister = autoFlushService.registerFlushHandler(flushUpdates);

    // Also set up a periodic cleanup for long-lived components
    const intervalId = setInterval(cleanupStaleUpdates, 10000); // Check every 10 seconds

    // Flush any pending updates when component unmounts
    return () => {
      clearInterval(intervalId);
      unregister(); // Unregister from auto-flush service
      if (hasPendingUpdates()) {
        flushUpdates();
      }
    };
  }, [cleanupStaleUpdates, flushUpdates, hasPendingUpdates]);

  // Combine regular and optimistic products with comprehensive error handling
  const cartItems = useMemo(() => {
    try {
      const regularProducts = [];

      // Safely process groups with comprehensive null checks
      if (Array.isArray(groups)) {
        for (const group of groups) {
          // Check if group exists and has required properties
          if (group && group.id && Array.isArray(group.items)) {
            try {
              const validProducts = group.items
                .filter((product: any) => {
                  // Ensure product exists and has required properties
                  return (
                    product &&
                    typeof product === "object" &&
                    typeof product.quantity === "number" &&
                    product.quantity > 0
                  );
                })
                .map((product: any) => ({
                  ...product,
                  groupId: group.id,
                  isOptimistic: false,
                }));

              regularProducts.push(...validProducts);
            } catch (groupError) {
              console.warn(
                `⚠️ Error processing group ${group.id}:`,
                groupError
              );
              // Continue processing other groups
            }
          } else {
            console.warn(`⚠️ Invalid group structure found:`, group);
          }
        }
      }

      // Safely process optimistic products
      const optimisticItems = [];
      if (Array.isArray(optimisticProducts)) {
        for (const item of optimisticProducts) {
          try {
            if (item && item.product && typeof item.product === "object") {
              optimisticItems.push({
                ...item.product,
                operationId: item.operationId,
                timestamp: item.timestamp,
                isOptimistic: true,
              });
            }
          } catch (optimisticError) {
            console.warn(
              `⚠️ Error processing optimistic product:`,
              optimisticError
            );
            // Continue processing other optimistic products
          }
        }
      }

      return [...regularProducts, ...optimisticItems];
    } catch (error) {
      console.error(`❌ Critical error in cartItems calculation:`, error);
      // Return empty array as fallback to prevent component crash
      return [];
    }
  }, [groups, optimisticProducts]);

  // Deduplicate items if necessary (in case optimistic updates get double-counted)
  const deduplicatedCartItems = useMemo(() => {
    try {
      const dedupMap = new Map();

      // Ensure cartItems is an array
      if (!Array.isArray(cartItems)) {
        console.warn(`⚠️ cartItems is not an array:`, cartItems);
        return [];
      }

      // First add all confirmed items
      cartItems
        .filter((item) => {
          // Ensure item exists and has required properties
          return (
            item &&
            typeof item === "object" &&
            !item.isOptimistic &&
            item.cartCustomId
          );
        })
        .forEach((item) => {
          try {
            dedupMap.set(item.cartCustomId, item);
          } catch (itemError) {
            console.warn(`⚠️ Error processing confirmed item:`, itemError);
          }
        });

      // Then add optimistic items only if there's no confirmed version
      cartItems
        .filter((item) => {
          // Ensure item exists and has required properties
          return (
            item &&
            typeof item === "object" &&
            item.isOptimistic &&
            item.cartCustomId
          );
        })
        .forEach((item) => {
          try {
            if (!dedupMap.has(item.cartCustomId)) {
              dedupMap.set(item.cartCustomId, item);
            }
          } catch (itemError) {
            console.warn(`⚠️ Error processing optimistic item:`, itemError);
          }
        });

      const result = Array.from(dedupMap.values());
      return result;
    } catch (error) {
      console.error(`❌ Critical error in deduplication:`, error);
      // Return empty array as fallback
      return [];
    }
  }, [cartItems]);

  const totalQuantity = useMemo(() => {
    try {
      // Ensure deduplicatedCartItems is an array
      if (!Array.isArray(deduplicatedCartItems)) {
        console.warn(
          `⚠️ deduplicatedCartItems is not an array for quantity calculation:`,
          deduplicatedCartItems
        );
        return 0;
      }

      return deduplicatedCartItems.reduce((acc: number, item: any) => {
        try {
          // Ensure item has required properties
          if (!item || typeof item !== "object" || !item.cartCustomId) {
            return acc;
          }

          const qty = getOptimisticQuantity(
            item.cartCustomId,
            item.quantity || 0
          );

          // Ensure qty is a valid number
          if (typeof qty === "number" && !isNaN(qty) && qty >= 0) {
            return acc + qty;
          }

          return acc;
        } catch (itemError) {
          console.warn(`⚠️ Error processing item quantity:`, itemError);
          return acc;
        }
      }, 0);
    } catch (error) {
      console.error(`❌ Critical error calculating total quantity:`, error);
      return 0;
    }
  }, [deduplicatedCartItems, getOptimisticQuantity]);

  // Calculate subtotal with optimistic updates priority
  const subtotal = useMemo(() => {
    try {
      // PRIORITY: Use optimistic calculations when there are pending updates, optimistic items, or active cart operations
      const hasOptimisticItems = deduplicatedCartItems.some(
        (item) => item.isOptimistic
      );
      const hasLocalPendingUpdates = hasPendingUpdates();

      // CRITICAL FIX: Check for any active cart operations to prevent premature fallback to server values
      const hasActiveCartOperations =
        Object.keys(cart.optimisticQuantities || {}).length > 0;

      const shouldUseOptimisticCalculation =
        hasLocalPendingUpdates || hasOptimisticItems || hasActiveCartOperations;

      // Enhanced logging to track the fix
      console.log(`🔄 [SUBTOTAL-LOGIC] Calculation decision:`, {
        hasLocalPendingUpdates,
        hasOptimisticItems,
        hasActiveCartOperations,
        shouldUseOptimisticCalculation,
        optimisticQuantitiesCount: Object.keys(cart.optimisticQuantities || {})
          .length,
        timestamp: new Date().toISOString(),
      });

      if (shouldUseOptimisticCalculation) {
        // Ensure deduplicatedCartItems is an array
        if (!Array.isArray(deduplicatedCartItems)) {
          console.warn(
            `⚠️ deduplicatedCartItems is not an array:`,
            deduplicatedCartItems
          );
          return totals?.finalSubtotal || 0;
        }

        // Create an array with optimistic quantities and prices for all items
        const optimisticItems = deduplicatedCartItems
          .map((item) => {
            try {
              // Ensure item has required properties
              if (!item || typeof item !== "object" || !item.cartCustomId) {
                return null;
              }

              const optimisticQuantity = getOptimisticQuantity(
                item.cartCustomId,
                item.quantity || 0
              );

              // Skip items with zero optimistic quantity
              if (optimisticQuantity <= 0) {
                return null;
              }

              // Calculate optimistic total price for this item using finalUnitPrice (includes discounts)
              const originalUnitPrice =
                item.pricing?.originalUnitPrice ??
                item.cleanPricing?.pricing?.originalUnitPrice ??
                0;

              const customizationSurcharge =
                item.pricing?.customizationSurcharge ??
                item.cleanPricing?.pricing?.customizationSurcharge ??
                0;

              // Use finalUnitPrice which includes promotional discounts
              const finalUnitPrice =
                item.pricing?.finalUnitPrice ??
                item.cleanPricing?.pricing?.finalUnitPrice ??
                originalUnitPrice + customizationSurcharge;

              const optimisticTotalPrice = getOptimisticTotalPrice(
                item.cartCustomId,
                finalUnitPrice, // ✅ Now includes promotional discounts
                optimisticQuantity
              );

              return {
                ...item,
                quantity: optimisticQuantity,
                optimisticTotalPrice,
              };
            } catch (itemError) {
              console.warn(
                `⚠️ Error processing item for optimistic subtotal:`,
                itemError
              );
              return null;
            }
          })
          .filter(Boolean); // Remove null items

        // Calculate optimistic subtotal by summing all optimistic total prices
        const optimisticSubtotal = optimisticItems.reduce((sum, item) => {
          return sum + (item.optimisticTotalPrice || 0);
        }, 0);

        // LOG: Track optimistic subtotal change
        const previousSubtotal = totals?.finalSubtotal || 0;
        logSubtotalChange(
          "MiniCart.optimisticCalculation",
          previousSubtotal,
          optimisticSubtotal,
          {
            hasOptimisticItems: optimisticItems.length,
            hasPendingUpdates: hasPendingUpdates(),
            optimisticItemDetails: optimisticItems.map((item) => ({
              cartCustomId: item.cartCustomId,
              quantity: item.quantity,
              optimisticTotalPrice: item.optimisticTotalPrice,
              isOptimistic: item.isOptimistic,
            })),
          },
          {
            trigger: "optimistic_calculation",
            totalItems: deduplicatedCartItems.length,
            activeItems: optimisticItems.length,
          }
        );

        console.log(
          `🔄 Using optimistic subtotal calculation: S/ ${optimisticSubtotal.toFixed(
            2
          )}`
        );
        return optimisticSubtotal;
      }

      // FALLBACK: Use server finalSubtotal when no optimistic updates
      if (totals && typeof totals.finalSubtotal === "number") {
        // LOG: Track server subtotal usage
        logSubtotalChange(
          "MiniCart.serverFallback",
          0, // No previous value in this context
          totals.finalSubtotal,
          {},
          {
            trigger: "server_fallback",
            hasOptimisticCalculation: false,
            totalItems: deduplicatedCartItems.length,
          }
        );

        console.log(
          `📊 Using server subtotal: S/ ${totals.finalSubtotal.toFixed(2)}`
        );
        return totals.finalSubtotal;
      }

      // LAST RESORT: Calculate from items using utility function
      if (!Array.isArray(deduplicatedCartItems)) {
        console.warn(
          `⚠️ deduplicatedCartItems is not an array:`,
          deduplicatedCartItems
        );
        return 0;
      }

      const nonZeroItems = deduplicatedCartItems.filter((item) => {
        try {
          return item && typeof item.quantity === "number" && item.quantity > 0;
        } catch (filterError) {
          console.warn(`⚠️ Error filtering zero quantity items:`, filterError);
          return false;
        }
      });

      const fallbackSubtotal = calculateItemsSubtotal(nonZeroItems);
      console.log(
        `⚠️ Using fallback subtotal calculation: S/ ${fallbackSubtotal.toFixed(
          2
        )}`
      );
      return fallbackSubtotal;
    } catch (error) {
      console.error(`❌ Critical error calculating subtotal:`, error);
      // Return server total or 0 as final fallback
      return totals?.finalSubtotal || 0;
    }
  }, [
    totals,
    deduplicatedCartItems,
    getOptimisticQuantity,
    getOptimisticTotalPrice,
    hasPendingUpdates,
    cart.optimisticQuantities,
  ]);

  // Check if any items have promotional discounts
  const itemsHavePromotionalDiscounts = useMemo(() => {
    try {
      // Ensure deduplicatedCartItems is an array
      if (!Array.isArray(deduplicatedCartItems)) {
        return false;
      }

      // Filter out items with zero quantity
      const activeItems = deduplicatedCartItems.filter((item) => {
        try {
          if (!item || typeof item !== "object" || !item.cartCustomId) {
            return false;
          }
          const optimisticQuantity = getOptimisticQuantity(
            item.cartCustomId,
            item.quantity || 0
          );
          return optimisticQuantity > 0;
        } catch (filterError) {
          console.warn(
            `⚠️ Error filtering items for promotional check:`,
            filterError
          );
          return false;
        }
      });

      // Check if any active items have promotional discounts
      return hasPromotionalDiscounts(activeItems);
    } catch (error) {
      console.error(`❌ Error checking promotional discounts:`, error);
      return false;
    }
  }, [deduplicatedCartItems, getOptimisticQuantity]);

  // Add a flag to mark if there are optimistic updates or pending changes
  const hasOptimisticUpdates = useMemo(() => {
    return (
      hasPendingUpdates() ||
      deduplicatedCartItems.some((item) => item.isOptimistic)
    );
  }, [deduplicatedCartItems, hasPendingUpdates]);

  const handleToggleSidenav = () => {
    // Flush any pending updates before closing the mini-cart
    if (hasPendingUpdates()) {
      flushUpdates();
    }

    setIsVisible(false);
    unlockBodyScroll();
    toggleSidenav();
  };

  // Calculate how long an optimistic product has been in the cart
  const getOptimisticAgeSeconds = (timestamp: number) => {
    if (!timestamp) return 0;
    return Math.round((Date.now() - timestamp) / 1000);
  };

  useEffect(() => {
    setIsVisible(true);
    lockBodyScroll();

    // Cleanup function
    return () => {
      unlockBodyScroll();
    };
  }, []);

  return (
    <div>
      {/* Show pending operations indicator when there are pending updates */}
      {hasPendingUpdates() && (
        <CartPendingIndicator
          pendingCount={getPendingUpdatesCount()}
          isVisible={true}
        />
      )}

      {/* Show error recovery for failed operations */}
      <CartErrorRecovery onRetry={() => window.location.reload()} />

      <div className={styles.overlay} onClick={handleToggleSidenav}></div>
      <div
        className={`${styles.miniCart} ${
          isVisible ? styles.visible : styles.hidden
        }`}
      >
        <TopHeader toggle={handleToggleSidenav} total={totalQuantity} />

        <Box
          sx={{
            flex: 1,
            overflowY: "auto",
            overflowX: "hidden",
            paddingBottom: deduplicatedCartItems.length ? "207px" : "75px",
          }}
        >
          {deduplicatedCartItems.length > 0 ? (
            deduplicatedCartItems.map((item: any) => (
              <MiniCartItem
                key={`${item.cartCustomId}${item.isOptimistic ? "-opt" : ""}`}
                item={{
                  ...item,
                  // Pass how old the optimistic update is
                  optimisticAge:
                    item.isOptimistic && item.timestamp
                      ? getOptimisticAgeSeconds(item.timestamp)
                      : 0,
                }}
                handleIncrease={() => handleIncrease(item)}
                handleDecrease={() => handleDecrease(item)}
                handleRemove={() => handleRemove(item)}
                getOptimisticQuantity={(defaultQty) =>
                  getOptimisticQuantity(item.cartCustomId, defaultQty)
                }
                getOptimisticPrice={(defaultPrice) =>
                  getOptimisticPrice(item.cartCustomId, defaultPrice)
                }
                getOptimisticTotalPrice={(defaultPrice, qty) =>
                  getOptimisticTotalPrice(item.cartCustomId, defaultPrice, qty)
                }
                toggleSidenav={handleToggleSidenav}
                handleRemoveOptimistic={
                  item.isOptimistic
                    ? () =>
                        dispatch(
                          removeOptimisticProduct({
                            cartCustomId: item.cartCustomId,
                            operationId: item.operationId,
                          })
                        )
                    : undefined
                }
              />
            ))
          ) : (
            <EmptyCartView />
          )}
        </Box>

        {deduplicatedCartItems.length > 0 && (
          <div className={styles.bottomActionsContainer}>
            <BottomActions
              subtotal={`S/ ${subtotal.toFixed(2)}`}
              hasOptimisticItems={hasOptimisticUpdates}
              hasPromotionalDiscounts={itemsHavePromotionalDiscounts}
              handleNavigate={navigate}
              toggleMiniCart={handleToggleSidenav}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default MiniCart;
